import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, User, Scissors, Calendar, RotateCw, X, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import WhatsAppButton from "@/components/WhatsAppButton";

const ACTION_LABELS = {
  criado: "Criado",
  remarcado: "Remarcado",
  status_alterado: "Status alterado",
  cancelado: "Cancelado",
  concluido: "Concluído",
};

export default function PendingAppointments() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ date: "", time: "", employee_id: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, eRes, cRes] = await Promise.all([
        apiCall("get", "/appointments/pending"),
        apiCall("get", "/employees"),
        apiCall("get", "/clients"),
      ]);
      setAppointments(pRes.data);
      setEmployees(eRes.data.filter((e) => e.active !== false));
      const map = {};
      for (const c of cRes.data) map[c.client_id] = c;
      setClientsMap(map);
    } catch {
      toast.error("Erro ao carregar agendamentos pendentes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenReschedule = (app) => {
    setSelected(app);
    const d = new Date(app.date);
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 1);
    setForm({
      date: nextWeek.toISOString().slice(0, 10),
      time: d.toTimeString().slice(0, 5),
      employee_id: app.employee_id || "",
      notes: "",
    });
    setRescheduleOpen(true);
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    try {
      const newDate = new Date(`${form.date}T${form.time}`).toISOString();
      await apiCall("put", `/appointments/${selected.appointment_id}/reschedule`, {
        date: newDate,
        employee_id: form.employee_id || null,
        notes: form.notes,
      });
      toast.success("Agendamento remarcado com sucesso");
      setRescheduleOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remarcar");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await apiCall("put", `/appointments/${id}?status=${status}`);
      toast.success("Status atualizado");
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const handleViewHistory = (app) => {
    setSelected(app);
    setHistoryOpen(true);
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  const formatCurrency = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  return (
    <div className="space-y-8" data-testid="pending-page">
      <PageHeader
        overline="Operações"
        title="Pendentes & Não Finalizados"
        subtitle="Agendamentos passados que ainda não foram finalizados — para remarcar ou ajustar"
      />

      {loading ? (
        <Card className="border border-border bg-card p-12 text-center text-muted-foreground shadow-soft rounded-md">
          Carregando...
        </Card>
      ) : appointments.length === 0 ? (
        <Card className="border border-border bg-card p-12 text-center shadow-soft rounded-md">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-muted-foreground">Nenhum agendamento pendente. Tudo em dia! ✨</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {appointments.map((app) => {
            const openInAgenda = () => {
              const d = new Date(app.date);
              const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
              navigate(`/agenda?date=${iso}&focus=${app.appointment_id}`);
            };
            return (
            <Card
              key={app.appointment_id}
              role="button"
              tabIndex={0}
              onClick={openInAgenda}
              className="border border-border bg-card p-5 shadow-soft rounded-md cursor-pointer hover:shadow-md hover:border-foreground/30 transition-all"
              data-testid={`pending-card-${app.appointment_id}`}
              title="Abrir comanda na agenda"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                  <span className="text-destructive">{formatDate(app.date)}</span>
                </div>
                <span
                  className="text-xs uppercase tracking-widest font-medium px-2 py-0.5 rounded"
                  style={{ background: "#FEE2E2", color: "#991B1B" }}
                >
                  {app.status}
                </span>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-medium">{app.client_name}</span>
                </div>
                {app.employee_name && (
                  <div className="flex items-center gap-2">
                    <Scissors className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-sm">{app.employee_name}</span>
                  </div>
                )}
                <div className="text-sm text-muted-foreground pl-6">
                  {app.services?.map((s) => s.service_name).join(", ") || "—"}
                </div>
                <div className="flex justify-between pt-2 border-t border-border text-sm">
                  <span className="text-muted-foreground">{app.duration_minutes} min</span>
                  <span className="font-medium" style={{ color: "#CBA871" }}>
                    {formatCurrency(app.total_price)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-2" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenReschedule(app)}
                  data-testid={`reschedule-${app.appointment_id}`}
                >
                  <RotateCw className="h-3 w-3 mr-1.5" strokeWidth={1.5} />
                  Remarcar
                </Button>
                <WhatsAppButton
                  toPhone={clientsMap[app.client_id]?.phone}
                  message={`Oi ${app.client_name}! Aqui é do salão. Sentimos sua falta no horário de ${new Date(app.date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}. Podemos combinar uma nova data? 💖`}
                  variant="outline"
                  size="sm"
                  buttonLabel="WhatsApp"
                  iconOnly={false}
                  className="text-emerald-700 hover:text-emerald-800 border-emerald-200"
                  testId={`whatsapp-${app.appointment_id}`}
                />
              </div>
              <div className="grid grid-cols-3 gap-2" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleStatusChange(app.appointment_id, "concluido")}
                  data-testid={`conclude-${app.appointment_id}`}
                >
                  Concluir
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleStatusChange(app.appointment_id, "cancelado")}
                  data-testid={`cancel-${app.appointment_id}`}
                >
                  <X className="h-3 w-3 mr-1.5" strokeWidth={1.5} />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleViewHistory(app)}
                  data-testid={`history-${app.appointment_id}`}
                >
                  <History className="h-3 w-3 mr-1.5" strokeWidth={1.5} />
                  Histórico
                </Button>
              </div>
            </Card>
          );})}
        </div>
      )}

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Remarcar Agendamento</DialogTitle>
          </DialogHeader>
          {selected && (
            <form onSubmit={handleReschedule} className="space-y-4 mt-4">
              <div className="p-3 bg-secondary/50 rounded-md text-sm">
                <div className="font-medium">{selected.client_name}</div>
                <div className="text-muted-foreground text-xs mt-1">
                  Originalmente: {formatDate(selected.date)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Nova Data</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                    data-testid="reschedule-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Nova Hora</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                    required
                    data-testid="reschedule-time-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Funcionário</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="reschedule-employee-select">
                    <SelectValue placeholder="Manter atual" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem
                        key={e.employee_id}
                        value={e.employee_id}
                        data-testid={`reschedule-employee-option-${e.employee_id}`}
                      >
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Motivo (opcional)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ex: Cliente solicitou"
                  data-testid="reschedule-notes-input"
                />
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setRescheduleOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="reschedule-save-button">
                  Confirmar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Histórico de Alterações</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 mt-4">
              {(selected.history || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Nenhum histórico registrado.
                </p>
              ) : (
                (selected.history || [])
                  .slice()
                  .reverse()
                  .map((entry, idx) => (
                    <div key={idx} className="border-l-2 border-accent pl-4 py-2" data-testid={`history-entry-${idx}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className="text-xs uppercase tracking-widest font-medium"
                          style={{ color: "#CBA871" }}
                        >
                          {ACTION_LABELS[entry.action] || entry.action}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(entry.changed_at)}
                        </span>
                      </div>
                      <div className="text-sm">{entry.notes}</div>
                      {entry.old_value && entry.new_value && entry.action === "remarcado" && (
                        <div className="text-xs text-muted-foreground mt-1">
                          De: {formatDate(entry.old_value)} → Para: {formatDate(entry.new_value)}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        Por <span className="font-medium text-foreground">{entry.changed_by_name}</span>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
